import React, {useEffect, useState, useRef } from "react";
import {
  Typography,
  Button,
} from "@material-tailwind/react";
import { useSelector, useDispatch } from "react-redux";
import { getRoles, getRolePermissions, togglePermission, updateRolePermissions } from '@/store/slices/roleSlice';
import ShowLoader  from "@/components/Loader";
import Toaster from "../../components/Toaster";
import useHasPermission from "../../components/useHasPermission";
import ScrollToTop from "@/components/ScrollToTop";
import { useTranslation } from "react-i18next";

export function RolePermission() {
    const dispatch = useDispatch();
    const { t } = useTranslation("role");
    
    const [activeRole, setActiveRole] = useState(null);

    const hasUpdatePermission = useHasPermission('role-permission.update');

    useEffect(() => {
        dispatch(getRoles());
    }, [dispatch]);

    const { list_roles, list_role_permissions, loading } = useSelector((state) => state.role);

    useEffect(() => {
        if (list_roles.data && list_roles.data.length > 0) {
            setActiveRole(list_roles.data[0]);
        }
    }, [list_roles]);

    useEffect(() => {
        if (activeRole) {
            dispatch(getRolePermissions(activeRole.id));
        }
    }, [activeRole, dispatch]);

    const handlePermissionChange = (permission_name) => {
        dispatch(togglePermission({permission_name}));
    };

    const handleUpdatePermissions = () => {
        const checkedPermissions = [];

        Object.values(list_role_permissions.data.permissions || {}).forEach((perms) => {
            perms.forEach((perm) => {
                if (perm.is_check === "yes") {
                    checkedPermissions.push(perm.name);
                }
            });
        });

        const payload = {
            name: list_role_permissions.data.name,
            role_id: list_role_permissions.data.id,
            permissions: checkedPermissions,
        };
        
        dispatch(updateRolePermissions({roleId:list_role_permissions.data.id , permissions:payload})).unwrap().then(() => {
            Toaster.success(t("toaster.updateSuccess"));
            dispatch(getRolePermissions(activeRole.id));
        }).catch((error) => {
            console.error("Update failed:", error);
            Toaster.error(t("toaster.updateFailed"));
        });
    };

    const handleCheckAllPermission = (permissions) => {
        const allChecked = isAllPermissionChecked(permissions);
        permissions.forEach((perm) => {
            const shouldBeChecked = !allChecked; 
            if ((shouldBeChecked && perm.is_check !== "yes") || (!shouldBeChecked && perm.is_check === "yes")) {
                dispatch(togglePermission({ permission_name: perm.name }));
            }
        });
    };
    
    const isAllPermissionChecked = (permissions) => {
        return permissions.every(
            (perm) => perm.is_check === "yes"
        );
    };

    return (
        <div className="mt-5 mb-8 flex flex-col gap-8">
            {loading && <ShowLoader /> }
            <ScrollToTop />
            <div className="mb-7 grid gap-y-10 gap-x-6 md:grid-cols-1 xl:grid-cols-[40%_57%] overflow-x-auto px-4 md:px-6">
                <div className="border border-blue-gray-100 rounded-xl">
                    <div className="m-0 p-4 flex justify-between">
                        <Typography variant="h6" color="blue-gray">
                            {t("roles")}
                        </Typography>
                    </div>
                    <div className="px-0 pt-0 pb-2">
                        <div className="flex lg:flex-col gap-3 overflow-x-auto lg:overflow-y-auto max-h-[500px] scrollbar-hide"> 
                            <ul className="flex flex-col gap-2 px-1">
                                {!list_roles?.data?.length ?
                                    <li key='0' className="flex items-center justify-between p-3 rounded-xl hover:bg-blue-50 cursor-pointer transition">{t("noData")}</li>
                                :
                                list_roles?.data.map((role, index) => (
                                    <li onClick={() => setActiveRole(role)}
                                    key={index}
                                        className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition ${activeRole?.id === role.id ? 'bg-blue-50' : 'hover:bg-blue-50'}`}
                                    >
                                        <div>
                                            <h3 className="text-sm font-medium text-gray-800 flex items-center">
                                                {/* {role.name} */}
                                                {t(role.name)}
                                            </h3>
                                        </div>
                                    </li>
                                ))
                                }
                            </ul>
                        </div>
                    </div>
                </div>
                <div className="border border-blue-gray-100 rounded-xl">
                    <div className="m-0 p-4 flex justify-between">
                        <Typography variant="h6" color="blue-gray">
                            {t("permissionsMatrix")}
                        </Typography>
                        <Typography variant="small" color="gray" className="-mb-3 font-medium">
                            {t("preconfigured")}
                        </Typography>
                    </div>
                    <div className="px-0 pt-0 pb-2">
                        <div className="flex lg:flex-col gap-3 overflow-x-auto lg:overflow-y-auto max-h-[500px] scrollbar-hide">
                            <table className="table-auto text-left">
                                <thead className="sticky top-0 z-30 mt-1 mb-6 p-5 bg-blue-gray-f5">
                                    <tr>
                                        <th className="p-4 border-b border-gray-200">{t("module")}</th>
                                        <th className="p-4 border-b border-gray-200 text-center">{t("all")}</th>
                                        <th className="p-4 border-b border-gray-200 text-center">{t("view")}</th>
                                        <th className="p-4 border-b border-gray-200 text-center">{t("add")}</th>
                                        <th className="p-4 border-b border-gray-200 text-center">{t("edit")}</th>
                                        <th className="p-4 border-b border-gray-200 text-center">{t("delete")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {list_role_permissions?.data?.permissions &&
                                    Object.entries(list_role_permissions.data.permissions).map(([module, perms]) => (
                                        <tr key={module} className="hover:bg-gray-50">
                                            {/* <td className="p-4 font-medium">{module}</td> */}
                                            <td className="p-4 font-medium">{t(module)}</td>
                                            <td className="p-4 text-center">
                                                <input
                                                    type="checkbox"
                                                    value="yes"
                                                    onChange={() => handleCheckAllPermission(perms)}
                                                    checked={isAllPermissionChecked(perms)}
                                                    className="w-4 h-4 cursor-pointer accent-green"
                                                />
                                                <p className="capitalize text-gray-500 text-sm">
                                                    {t("all")}
                                                </p>
                                            </td>
                                            {perms.map((perm) => (
                                                <td key={perm.name} className="p-4 text-center">
                                                    <input
                                                        type="checkbox"
                                                        value="yes"
                                                        checked={perm.is_check === "yes"}
                                                        className="w-4 h-4 accent-green cursor-pointer"
                                                        onChange={() => handlePermissionChange(perm.name)}
                                                    />
                                                    <p className="capitalize text-gray-500 text-sm">
                                                        {t(perm.name.split('.').pop() === 'update' ? 'edit' : perm.name.split('.').pop())}
                                                    </p>
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {(hasUpdatePermission && activeRole?.name !== 'Administrator') && 
                            <Button variant="outlined" className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white m-4" onClick={handleUpdatePermissions}>{t("updatePermissions")}</Button>
                        }
                    </div>
                </div>
            </div>
        </div>
    );
}

export default RolePermission;
